import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import type { AboutSection } from '../../../lib/supabase';

type Lang = 'ko' | 'en' | 'vi';

const LANGS: { key: Lang; label: string }[] = [
  { key: 'ko', label: '한국어' },
  { key: 'en', label: 'English' },
  { key: 'vi', label: 'Tiếng Việt' },
];

interface SectionConfig {
  key: string;
  label: string;
  group: string;
  fields: { key: string; label: string; multiline?: boolean }[];
}

const SECTIONS: SectionConfig[] = [
  {
    key: 'hero',
    label: '히어로 배너',
    group: '기본',
    fields: [
      { key: 'eyebrow', label: '상단 소제목' },
      { key: 'heading', label: '메인 제목' },
      { key: 'desc', label: '설명', multiline: true },
    ],
  },
  {
    key: 'story',
    label: '우리 이야기',
    group: '스토리',
    fields: [
      { key: 'eyebrow', label: '상단 소제목' },
      { key: 'heading', label: '섹션 제목' },
      { key: 'p1', label: '본문 1', multiline: true },
      { key: 'p2', label: '본문 2', multiline: true },
      { key: 'p3', label: '본문 3', multiline: true },
      { key: 'cta', label: 'CTA 버튼 텍스트' },
    ],
  },
  {
    key: 'values_header',
    label: '가치 헤더',
    group: '미션 & 가치',
    fields: [
      { key: 'eyebrow', label: '상단 소제목' },
      { key: 'heading', label: '섹션 제목' },
      { key: 'sub', label: '서브 텍스트' },
    ],
  },
  {
    key: 'value_partnership',
    label: '가치 1 — 파트너십',
    group: '미션 & 가치',
    fields: [
      { key: 'title', label: '카드 제목' },
      { key: 'desc', label: '카드 설명', multiline: true },
    ],
  },
  {
    key: 'value_integrity',
    label: '가치 2 — 정직',
    group: '미션 & 가치',
    fields: [
      { key: 'title', label: '카드 제목' },
      { key: 'desc', label: '카드 설명', multiline: true },
    ],
  },
  {
    key: 'value_innovation',
    label: '가치 3 — 혁신',
    group: '미션 & 가치',
    fields: [
      { key: 'title', label: '카드 제목' },
      { key: 'desc', label: '카드 설명', multiline: true },
    ],
  },
  {
    key: 'value_local',
    label: '가치 4 — 현지 전문성',
    group: '미션 & 가치',
    fields: [
      { key: 'title', label: '카드 제목' },
      { key: 'desc', label: '카드 설명', multiline: true },
    ],
  },
  {
    key: 'milestones_header',
    label: '연혁 섹션 헤더',
    group: '회사 연혁',
    fields: [
      { key: 'eyebrow', label: '상단 소제목' },
      { key: 'heading', label: '섹션 제목' },
    ],
  },
];

const GROUPS = ['기본', '스토리', '미션 & 가치'];

interface MilestoneRow {
  sectionKey: string;
  year: string;
  content_i18n: Record<Lang, Record<string, string>>;
}

interface MilestoneEditState {
  originalKey: string | null;
  year: string;
  data: Record<Lang, { title: string; desc: string }>;
}

interface Props {
  showMsg: (msg: string) => void;
}

export default function AboutEditor({ showMsg }: Props) {
  const [sections, setSections] = useState<Record<string, AboutSection>>({});
  const [milestones, setMilestones] = useState<MilestoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLang, setEditLang] = useState<Lang>('ko');
  const [editData, setEditData] = useState<Record<Lang, Record<string, string>>>({
    ko: {}, en: {}, vi: {},
  });

  // Milestone edit state
  const [msEdit, setMsEdit] = useState<MilestoneEditState | null>(null);
  const [msEditLang, setMsEditLang] = useState<Lang>('ko');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('about_content').select('*');
    if (data) {
      const map: Record<string, AboutSection> = {};
      const ms: MilestoneRow[] = [];
      (data as AboutSection[]).forEach((row) => {
        if (row.section_key.startsWith('milestone_') && row.section_key !== 'milestones_header') {
          const year = row.section_key.replace('milestone_', '');
          const ci = (row.content_i18n || {}) as Record<Lang, Record<string, string>>;
          ms.push({ sectionKey: row.section_key, year, content_i18n: ci });
        } else {
          map[row.section_key] = row;
        }
      });
      ms.sort((a, b) => parseInt(a.year) - parseInt(b.year));
      setSections(map);
      setMilestones(ms);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEdit = (sectionKey: string) => {
    const sec = sections[sectionKey];
    const i18n = (sec?.content_i18n || {}) as Record<Lang, Record<string, string>>;
    setEditData({
      ko: { ...(i18n.ko || {}) },
      en: { ...(i18n.en || {}) },
      vi: { ...(i18n.vi || {}) },
    });
    setEditLang('ko');
    setEditingKey(sectionKey);
  };

  const updateField = (lang: Lang, field: string, value: string) => {
    setEditData(prev => ({ ...prev, [lang]: { ...prev[lang], [field]: value } }));
  };

  const saveSection = async () => {
    if (!editingKey) return;
    setSaving(true);
    const exists = !!sections[editingKey];
    if (exists) {
      await supabase.from('about_content').update({ content_i18n: editData }).eq('section_key', editingKey);
    } else {
      await supabase.from('about_content').insert({ section_key: editingKey, content_i18n: editData });
    }
    await fetchData();
    setEditingKey(null);
    setSaving(false);
    showMsg('저장됐어요!');
  };

  // Milestone CRUD
  const openMilestoneEdit = (ms: MilestoneRow | null) => {
    if (ms) {
      setMsEdit({
        originalKey: ms.sectionKey,
        year: ms.year,
        data: {
          ko: { title: ms.content_i18n?.ko?.title || '', desc: ms.content_i18n?.ko?.desc || '' },
          en: { title: ms.content_i18n?.en?.title || '', desc: ms.content_i18n?.en?.desc || '' },
          vi: { title: ms.content_i18n?.vi?.title || '', desc: ms.content_i18n?.vi?.desc || '' },
        },
      });
    } else {
      setMsEdit({
        originalKey: null,
        year: '',
        data: {
          ko: { title: '', desc: '' },
          en: { title: '', desc: '' },
          vi: { title: '', desc: '' },
        },
      });
    }
    setMsEditLang('ko');
  };

  const updateMsField = (lang: Lang, field: 'title' | 'desc', value: string) => {
    setMsEdit(prev => {
      if (!prev) return prev;
      return { ...prev, data: { ...prev.data, [lang]: { ...prev.data[lang], [field]: value } } };
    });
  };

  const saveMilestone = async () => {
    if (!msEdit) return;
    const year = msEdit.year.trim();
    if (!year || !/^\d{4}$/.test(year)) {
      showMsg('연도는 4자리 숫자로 입력해주세요.');
      return;
    }
    setSaving(true);
    const newKey = `milestone_${year}`;
    const content_i18n = {
      ko: { title: msEdit.data.ko.title, desc: msEdit.data.ko.desc },
      en: { title: msEdit.data.en.title, desc: msEdit.data.en.desc },
      vi: { title: msEdit.data.vi.title, desc: msEdit.data.vi.desc },
    };

    if (msEdit.originalKey && msEdit.originalKey !== newKey) {
      // Year changed → delete old, insert new
      await supabase.from('about_content').delete().eq('section_key', msEdit.originalKey);
      await supabase.from('about_content').insert({ section_key: newKey, content_i18n });
    } else if (msEdit.originalKey) {
      // Update existing
      await supabase.from('about_content').update({ content_i18n }).eq('section_key', newKey);
    } else {
      // Insert new
      await supabase.from('about_content').insert({ section_key: newKey, content_i18n });
    }

    await fetchData();
    setMsEdit(null);
    setSaving(false);
    showMsg('연혁이 저장됐어요!');
  };

  const deleteMilestone = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await supabase.from('about_content').delete().eq('section_key', deleteTarget);
    await fetchData();
    setDeleteTarget(null);
    setDeleting(false);
    showMsg('삭제됐어요!');
  };

  const editingConfig = SECTIONS.find(s => s.key === editingKey);

  if (loading) {
    return <div className="text-center py-20 text-navy/30 font-sans text-sm">불러오는 중...</div>;
  }

  return (
    <div>
      <h2 className="font-serif text-navy text-xl font-bold mb-6">About 페이지 콘텐츠</h2>
      <p className="text-navy/40 text-xs font-sans mb-8">섹션을 클릭해 한국어 / 영어 / 베트남어 텍스트를 편집하세요.</p>

      {GROUPS.map(group => (
        <div key={group} className="mb-8">
          <p className="text-xs font-sans text-navy/40 tracking-widest uppercase mb-3">{group}</p>
          <div className="space-y-2">
            {SECTIONS.filter(s => s.group === group).map(sec => {
              const data = sections[sec.key];
              const koContent = (data?.content_i18n as Record<string, Record<string, string>>)?.ko;
              const previewText = koContent ? Object.values(koContent)[0] : '';
              return (
                <div key={sec.key} className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-navy text-sm font-sans font-semibold">{sec.label}</p>
                    <p className="text-navy/40 text-xs font-sans truncate mt-0.5">{previewText || '(내용 없음)'}</p>
                  </div>
                  <button
                    onClick={() => openEdit(sec.key)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-edit-line text-navy/50 text-sm" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* 회사 연혁 섹션 헤더 */}
      <div className="mb-4">
        <p className="text-xs font-sans text-navy/40 tracking-widest uppercase mb-3">회사 연혁</p>
        {/* 연혁 섹션 헤더 편집 */}
        {SECTIONS.filter(s => s.group === '회사 연혁').map(sec => {
          const data = sections[sec.key];
          const koContent = (data?.content_i18n as Record<string, Record<string, string>>)?.ko;
          const previewText = koContent ? Object.values(koContent)[0] : '';
          return (
            <div key={sec.key} className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center gap-4 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-navy text-sm font-sans font-semibold">{sec.label}</p>
                <p className="text-navy/40 text-xs font-sans truncate mt-0.5">{previewText || '(내용 없음)'}</p>
              </div>
              <button
                onClick={() => openEdit(sec.key)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-edit-line text-navy/50 text-sm" />
              </button>
            </div>
          );
        })}
      </div>

      {/* 연혁 항목 목록 */}
      <div className="bg-[#F8F9FB] rounded-2xl p-5 mb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-navy text-sm font-sans font-semibold">연혁 항목 ({milestones.length}개)</p>
          <button
            onClick={() => openMilestoneEdit(null)}
            className="flex items-center gap-1.5 bg-navy text-white text-xs font-sans font-medium px-3.5 py-2 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            새 연혁 추가
          </button>
        </div>

        {milestones.length === 0 ? (
          <p className="text-center text-navy/30 text-sm font-sans py-6">연혁 항목이 없어요.</p>
        ) : (
          <div className="space-y-2">
            {milestones.map(ms => (
              <div key={ms.sectionKey} className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ backgroundColor: '#0A1628' }}
                >
                  <span className="text-white text-xs font-sans font-bold">{ms.year}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-navy text-sm font-sans font-semibold">{ms.content_i18n?.ko?.title || '(제목 없음)'}</p>
                  <p className="text-navy/40 text-xs font-sans truncate mt-0.5">{ms.content_i18n?.ko?.desc || '(내용 없음)'}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openMilestoneEdit(ms)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line text-navy/50 text-sm" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(ms.sectionKey)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-red-400 text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section Edit Modal */}
      {editingKey && editingConfig && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 overflow-y-auto py-6">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg my-auto">
            <h3 className="font-serif text-navy text-lg font-bold mb-1">{editingConfig.label}</h3>
            <p className="text-navy/40 text-xs font-sans mb-5">언어를 선택하고 내용을 편집하세요.</p>

            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-sans text-navy/50">콘텐츠 언어</span>
              <div className="flex gap-1">
                {LANGS.map(l => (
                  <button
                    key={l.key}
                    onClick={() => setEditLang(l.key)}
                    className={`px-3 py-1 rounded text-xs font-sans cursor-pointer whitespace-nowrap transition-all ${editLang === l.key ? 'bg-navy text-white' : 'bg-gray-100 text-navy/50 hover:bg-gray-200'}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3 border border-gray-100 rounded-xl p-4 bg-[#F8F9FB]">
              {editingConfig.fields.map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-sans text-navy/50 mb-1.5">{field.label}</label>
                  {field.multiline ? (
                    <textarea
                      value={editData[editLang][field.key] || ''}
                      onChange={e => updateField(editLang, field.key, e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy resize-none bg-white"
                    />
                  ) : (
                    <input
                      value={editData[editLang][field.key] || ''}
                      onChange={e => updateField(editLang, field.key, e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy bg-white"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingKey(null)}
                className="flex-1 border border-gray-200 text-navy/50 text-sm font-sans py-3 rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={saveSection}
                disabled={saving}
                className="flex-1 bg-navy text-white text-sm font-sans font-semibold py-3 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Milestone Edit Modal */}
      {msEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 overflow-y-auto py-6">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg my-auto">
            <h3 className="font-serif text-navy text-lg font-bold mb-1">
              {msEdit.originalKey ? '연혁 수정' : '새 연혁 추가'}
            </h3>
            <p className="text-navy/40 text-xs font-sans mb-6">연도와 각 언어별 제목·내용을 입력하세요.</p>

            {/* Year field */}
            <div className="mb-5">
              <label className="block text-xs font-sans text-navy/50 mb-1.5 font-medium">연도 (4자리)</label>
              <input
                value={msEdit.year}
                onChange={e => setMsEdit(prev => prev ? { ...prev, year: e.target.value } : prev)}
                placeholder="예: 2025"
                maxLength={4}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy bg-white"
              />
            </div>

            {/* Language Tabs */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-sans text-navy/50">콘텐츠 언어</span>
              <div className="flex gap-1">
                {LANGS.map(l => (
                  <button
                    key={l.key}
                    onClick={() => setMsEditLang(l.key)}
                    className={`px-3 py-1 rounded text-xs font-sans cursor-pointer whitespace-nowrap transition-all ${msEditLang === l.key ? 'bg-navy text-white' : 'bg-gray-100 text-navy/50 hover:bg-gray-200'}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3 border border-gray-100 rounded-xl p-4 bg-[#F8F9FB]">
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">제목</label>
                <input
                  value={msEdit.data[msEditLang].title}
                  onChange={e => updateMsField(msEditLang, 'title', e.target.value)}
                  placeholder="예: 서울에서 설립"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">내용</label>
                <textarea
                  value={msEdit.data[msEditLang].desc}
                  onChange={e => updateMsField(msEditLang, 'desc', e.target.value)}
                  placeholder="이 연도에 있었던 주요 사건을 설명하세요."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy resize-none bg-white"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setMsEdit(null)}
                className="flex-1 border border-gray-200 text-navy/50 text-sm font-sans py-3 rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={saveMilestone}
                disabled={saving}
                className="flex-1 bg-navy text-white text-sm font-sans font-semibold py-3 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl" />
            </div>
            <h3 className="font-serif text-navy text-lg font-bold text-center mb-2">연혁 삭제</h3>
            <p className="text-navy/50 text-sm font-sans text-center mb-6">
              <strong>{deleteTarget.replace('milestone_', '')}년</strong> 연혁을 삭제할까요? 되돌릴 수 없어요.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 border border-gray-200 text-navy/50 text-sm font-sans py-3 rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={deleteMilestone}
                disabled={deleting}
                className="flex-1 bg-red-500 text-white text-sm font-sans font-semibold py-3 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
